import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { DeadLetter, DeadLetterStatus, InboxDisposition, OutboxStatus, Partner, ReconciliationDifference, ReconciliationDifferencePage, ReviewStatus } from '@regigov/contracts';
import { exchangeApi } from '../api/exchange';
import { DefinitionList, Field, ProblemAlert, StatusBadge, formatDateTime } from './ui';

const BASE = '/cer/exchange';
const DIFFERENCE_PAGE = 50;
const PARTNERS: Partner[] = ['aemo', 'stc', 'network'];
const PARTNER_LABEL: Record<Partner, string> = { aemo: 'AEMO', stc: 'STC registry', network: 'Network operator' };
const OUTBOX_STATUSES: OutboxStatus[] = ['pending', 'publishing', 'delivered', 'failed', 'dead'];
const DISPOSITIONS: InboxDisposition[] = ['applied', 'late', 'duplicate', 'replayed', 'malformed', 'failed'];
const DLQ_STATUSES: DeadLetterStatus[] = ['open', 'replayed', 'discarded'];

const TABS = [
  ['catalogue', 'Interface catalogue'],
  ['outbox', 'Outbox'],
  ['inbox', 'Inbox'],
  ['dead-letters', 'Dead letters'],
  ['reconciliation', 'Reconciliation'],
] as const;
type Tab = (typeof TABS)[number][0];

function useFilters() {
  const [params, setParams] = useSearchParams();
  function update(next: Record<string, string>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p);
  }
  return { params, update };
}

function PartnerSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Partner filter">
      <option value="">All partners</option>
      {PARTNERS.map((p) => (
        <option key={p} value={p}>
          {PARTNER_LABEL[p]}
        </option>
      ))}
    </select>
  );
}

function Json({ value }: { value: unknown }) {
  return <pre>{JSON.stringify(value ?? null, null, 2)}</pre>;
}

function entityLink(kind: string | null, id: string | null) {
  if (!kind || !id) return <span className="muted">—</span>;
  const path = kind === 'model' ? 'models' : kind === 'unit' ? 'units' : 'installations';
  return (
    <Link to={`/cer/register/${path}/${id}`}>
      <code>{id}</code>
    </Link>
  );
}

export function ExchangePage() {
  const { params, update } = useFilters();
  const tab = (TABS.find(([t]) => t === params.get('tab'))?.[0] ?? 'catalogue') as Tab;
  const summary = useQuery({ queryKey: ['exchange', 'summary'], queryFn: exchangeApi.summary, refetchInterval: 15_000 });

  return (
    <>
      <h1>Information exchange</h1>
      <p className="muted">
        Versioned contracts with AEMO, the STC registry and the network operator. Register changes are queued in the outbox inside the same transaction and delivered by the worker;
        partner messages land in the inbox where duplicates, replays and late messages are recognised and malformed ones are dead-lettered for replay. Reconciliation compares the
        register with each partner and records differences for review — nothing changes the register automatically.
        Business events exported to the data lake are tracked under <Link to={`${BASE}/lake`}>Data lake export</Link>.
      </p>
      <ProblemAlert error={summary.error} />
      {summary.data && (
        <div className="cards">
          <section className="card">
            <h2>Outbox</h2>
            <p>{OUTBOX_STATUSES.map((s) => `${summary.data.outbox[s] ?? 0} ${s}`).join(' · ')}</p>
          </section>
          <section className="card">
            <h2>Inbox</h2>
            <p>{DISPOSITIONS.map((s) => `${summary.data.inbox[s] ?? 0} ${s}`).join(' · ')}</p>
          </section>
          <section className="card">
            <h2>Dead letters</h2>
            <p>{DLQ_STATUSES.map((s) => `${summary.data.deadLetters[s] ?? 0} ${s}`).join(' · ')}</p>
          </section>
          <section className="card">
            <h2>Last reconciliation</h2>
            <p>
              {PARTNERS.map((p) => {
                const run = summary.data.lastReconciliation[p];
                return (
                  <span key={p}>
                    {PARTNER_LABEL[p]}: {run ? <Link to={`${BASE}/reconciliation/${run.id}`}>{`${run.differences} differences, ${formatDateTime(run.startedAt)}`}</Link> : 'never'}
                    <br />
                  </span>
                );
              })}
            </p>
          </section>
        </div>
      )}
      <nav className="tabs" aria-label="Exchange sections">
        {TABS.map(([t, label]) => (
          <button key={t} type="button" className={t === tab ? 'tab active' : 'tab'} onClick={() => update({ tab: t, status: '', disposition: '' })}>
            {label}
          </button>
        ))}
      </nav>
      {tab === 'catalogue' && <CatalogueTab />}
      {tab === 'outbox' && <OutboxTab />}
      {tab === 'inbox' && <InboxTab />}
      {tab === 'dead-letters' && <DeadLettersTab />}
      {tab === 'reconciliation' && <ReconciliationTab />}
    </>
  );
}

function CatalogueTab() {
  const list = useQuery({ queryKey: ['exchange', 'interfaces'], queryFn: exchangeApi.interfaces });
  return (
    <>
      <p className="muted">
        One row per partner interface and contract version. v1 and v2 of the AEMO device record coexist: both are published for every unit change until v1 is retired. The same
        catalogue is generated into <code>docs/interface-catalogue.md</code>.
      </p>
      <ProblemAlert error={list.error} />
      {list.data && (
        <table className="table">
          <thead>
            <tr>
              <th>Interface</th>
              <th>Partner</th>
              <th>Direction</th>
              <th>Transport</th>
              <th>Version</th>
              <th>Status</th>
              <th>Events</th>
              <th>Behaviour</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((i) => (
              <tr key={`${i.id}@${i.version}`}>
                <td>
                  <strong>{i.id}</strong>
                  <br />
                  <span className="muted">{i.description}</span>
                  <br />
                  <code>{i.endpoint}</code>
                </td>
                <td>{PARTNER_LABEL[i.partner]}</td>
                <td>{i.direction}</td>
                <td>{i.transport}</td>
                <td>{i.version}</td>
                <td>
                  <StatusBadge status={i.status} />
                </td>
                <td>{i.eventTypes.join(', ')}</td>
                <td>
                  <details>
                    <summary>Auth, idempotency, retry, DLQ, reconciliation</summary>
                    <DefinitionList
                      items={[
                        ['Authentication', i.auth],
                        ['Idempotency', i.idempotency],
                        ['Retry', i.retry],
                        ['Dead letter / replay', i.deadLetter],
                        ['Reconciliation', i.reconciliation],
                        ['Fields', i.fields.join(', ')],
                      ]}
                    />
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function OutboxTab() {
  const qc = useQueryClient();
  const { params, update } = useFilters();
  const backfill = useMutation({ mutationFn: exchangeApi.publishUnpublished, onSuccess: () => void qc.invalidateQueries({ queryKey: ['exchange'] }) });
  const partner = params.get('partner') ?? '';
  const status = params.get('status') ?? '';
  const entityId = params.get('entityId') ?? '';
  const [draft, setDraft] = useState(entityId);
  const query = { partner: (partner || undefined) as Partner | undefined, status: (status || undefined) as OutboxStatus | undefined, entityId: entityId || undefined };
  const list = useQuery({ queryKey: ['exchange', 'outbox', query], queryFn: () => exchangeApi.outbox(query), refetchInterval: 10_000 });
  return (
    <>
      <form
        className="row"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          update({ entityId: draft });
        }}
      >
        <input className="grow" placeholder="Filter by register id (model, unit or installation)" value={draft} onChange={(e) => setDraft(e.target.value)} />
        <PartnerSelect value={partner} onChange={(v) => update({ partner: v })} />
        <select value={status} onChange={(e) => update({ status: e.target.value })} aria-label="Status filter">
          <option value="">All statuses</option>
          {OUTBOX_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button type="submit">Filter</button>
        <button type="button" className="secondary" disabled={backfill.isPending} onClick={() => backfill.mutate()} title="Queue the current version of every unit and installation that has never been published (seeded or migrated data)">
          Publish unpublished records
        </button>
      </form>
      {backfill.data && <p className="notice">Queued {backfill.data.queued} messages for the worker.</p>}
      <ProblemAlert error={list.error ?? backfill.error} />
      {list.data && list.data.items.length === 0 && <p className="muted">No outbox messages match.</p>}
      {list.data && list.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Partner / interface</th>
              <th>Event</th>
              <th>Entity</th>
              <th>Status</th>
              <th>Attempts</th>
              <th>Queued</th>
              <th>Delivered / next attempt</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((m) => (
              <tr key={m.id}>
                <td>
                  <Link to={`${BASE}/outbox/${m.id}`}>{m.id}</Link>
                </td>
                <td>
                  {PARTNER_LABEL[m.partner]}
                  <br />
                  <code>
                    {m.interfaceId} {m.schemaVersion}
                  </code>
                </td>
                <td>{m.eventType}</td>
                <td>
                  {entityLink(m.entityKind, m.entityId)} <span className="muted">v{m.entityVersion}</span>
                </td>
                <td>
                  <StatusBadge status={m.status} />
                </td>
                <td>{m.attempts}</td>
                <td>{formatDateTime(m.createdAt)}</td>
                <td>{m.status === 'delivered' ? formatDateTime(m.deliveredAt) : m.status === 'dead' ? '—' : formatDateTime(m.nextAttemptAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function OutboxMessagePage() {
  const { id } = useParams();
  const query = useQuery({ queryKey: ['exchange', 'outbox', 'item', id], queryFn: () => exchangeApi.outboxMessage(Number(id)) });
  const m = query.data;
  return (
    <>
      <p>
        <Link to={`${BASE}?tab=outbox`}>← Outbox</Link>
      </p>
      <ProblemAlert error={query.error} />
      {m && (
        <>
          <div className="row">
            <h1>Outbox message {m.id}</h1>
            <StatusBadge status={m.status} />
          </div>
          <DefinitionList
            items={[
              ['Partner', PARTNER_LABEL[m.partner]],
              ['Interface', `${m.interfaceId} ${m.schemaVersion}`],
              ['Event', m.eventType],
              ['Entity', <>{entityLink(m.entityKind, m.entityId)} version {m.entityVersion}</>],
              ['Message id', <code>{m.messageId}</code>],
              ['Idempotency key', <code>{m.idempotencyKey}</code>],
              ['Correlation id', m.correlationId ? <code>{m.correlationId}</code> : null],
              ['Effective', formatDateTime(m.effectiveAt)],
              ['Queued', formatDateTime(m.createdAt)],
              ['Delivered', formatDateTime(m.deliveredAt)],
              ['Attempts', String(m.attempts)],
              ['Next attempt', m.status === 'failed' || m.status === 'pending' ? formatDateTime(m.nextAttemptAt) : null],
              ['Last error', m.lastError],
            ]}
          />
          <h2>Delivery attempts</h2>
          {(m.deliveries ?? []).length === 0 && <p className="muted">Not yet attempted.</p>}
          {(m.deliveries ?? []).length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Attempt</th>
                  <th>At</th>
                  <th>Outcome</th>
                  <th>HTTP</th>
                  <th>Response</th>
                </tr>
              </thead>
              <tbody>
                {m.deliveries!.map((d) => (
                  <tr key={d.id}>
                    <td>{d.attempt}</td>
                    <td>{formatDateTime(d.attemptedAt)}</td>
                    <td>
                      <StatusBadge status={d.outcome} />
                    </td>
                    <td>{d.statusCode ?? '—'}</td>
                    <td>
                      <code>{d.response ?? ''}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <h2>Payload as sent</h2>
          <Json value={m.payload} />
        </>
      )}
    </>
  );
}

function InboxTab() {
  const { params, update } = useFilters();
  const partner = params.get('partner') ?? '';
  const disposition = params.get('disposition') ?? '';
  const entityId = params.get('entityId') ?? '';
  const [draft, setDraft] = useState(entityId);
  const query = { partner: (partner || undefined) as Partner | undefined, disposition: (disposition || undefined) as InboxDisposition | undefined, entityId: entityId || undefined };
  const list = useQuery({ queryKey: ['exchange', 'inbox', query], queryFn: () => exchangeApi.inbox(query), refetchInterval: 10_000 });
  return (
    <>
      <form
        className="row"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          update({ entityId: draft });
        }}
      >
        <input className="grow" placeholder="Filter by register id" value={draft} onChange={(e) => setDraft(e.target.value)} />
        <PartnerSelect value={partner} onChange={(v) => update({ partner: v })} />
        <select value={disposition} onChange={(e) => update({ disposition: e.target.value })} aria-label="Disposition filter">
          <option value="">All dispositions</option>
          {DISPOSITIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button type="submit">Filter</button>
      </form>
      <p className="muted">
        <strong>applied</strong> updated the entity's partner facts · <strong>late</strong> was older than the fact already held · <strong>duplicate</strong> repeated an idempotency
        key · <strong>replayed</strong> repeated a message id · <strong>malformed</strong> / <strong>failed</strong> were dead-lettered.
      </p>
      <ProblemAlert error={list.error} />
      {list.data && list.data.items.length === 0 && <p className="muted">No inbox messages match.</p>}
      {list.data && list.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Partner / event</th>
              <th>Transport</th>
              <th>Disposition</th>
              <th>Entity</th>
              <th>Effective</th>
              <th>Received</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((m) => (
              <tr key={m.id}>
                <td>
                  <Link to={`${BASE}/inbox/${m.id}`}>{m.id}</Link>
                  {m.replayOf && <span className="muted"> (replay of {m.replayOf})</span>}
                </td>
                <td>
                  {PARTNER_LABEL[m.partner]}
                  <br />
                  <code>
                    {m.eventType} {m.schemaVersion}
                  </code>
                </td>
                <td>
                  {m.transport}
                  {m.sourceReference && (
                    <>
                      <br />
                      <span className="muted">{m.sourceReference}</span>
                    </>
                  )}
                </td>
                <td>
                  <StatusBadge status={m.disposition} />
                </td>
                <td>{entityLink(m.entityKind, m.entityId)}</td>
                <td>{m.effectiveDate ?? '—'}</td>
                <td>{formatDateTime(m.receivedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function InboxMessagePage() {
  const { id } = useParams();
  const query = useQuery({ queryKey: ['exchange', 'inbox', 'item', id], queryFn: () => exchangeApi.inboxMessage(Number(id)) });
  const m = query.data;
  return (
    <>
      <p>
        <Link to={`${BASE}?tab=inbox`}>← Inbox</Link>
      </p>
      <ProblemAlert error={query.error} />
      {m && (
        <>
          <div className="row">
            <h1>Inbox message {m.id}</h1>
            <StatusBadge status={m.disposition} />
          </div>
          <DefinitionList
            items={[
              ['Partner', PARTNER_LABEL[m.partner]],
              ['Interface', m.interfaceId ? `${m.interfaceId} ${m.schemaVersion}` : m.schemaVersion],
              ['Event', m.eventType],
              ['Transport', `${m.transport}${m.sourceReference ? ` (${m.sourceReference})` : ''}`],
              ['Message id', <code>{m.messageId}</code>],
              ['Idempotency key', <code>{m.idempotencyKey}</code>],
              ['Producer timestamp', formatDateTime(m.producerTimestamp)],
              ['Effective date', m.effectiveDate],
              ['Received', formatDateTime(m.receivedAt)],
              ['Entity', entityLink(m.entityKind, m.entityId)],
              ['Replay of', m.replayOf ? <Link to={`${BASE}/inbox/${m.replayOf}`}>inbox message {m.replayOf}</Link> : null],
            ]}
          />
          <h2>Disposition detail</h2>
          <Json value={m.detail} />
          <h2>Message as received</h2>
          <Json value={m.payload} />
        </>
      )}
    </>
  );
}

function DeadLettersTab() {
  const { params, update } = useFilters();
  const partner = params.get('partner') ?? '';
  const status = params.get('status') || 'open';
  const query = { partner: (partner || undefined) as Partner | undefined, status: (status || undefined) as DeadLetterStatus | undefined };
  const list = useQuery({ queryKey: ['exchange', 'dead-letters', query], queryFn: () => exchangeApi.deadLetters(query), refetchInterval: 10_000 });
  return (
    <>
      <div className="row">
        <PartnerSelect value={partner} onChange={(v) => update({ partner: v })} />
        <select value={status} onChange={(e) => update({ status: e.target.value })} aria-label="Status filter">
          {DLQ_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <p className="muted">
        Inbound messages that failed validation or could not be matched to the register, and outbound messages that exhausted their retries. Replay re-runs the message through the
        normal path — a replayed inbound message is still subject to duplicate and late checks, so replaying never applies a change twice.
      </p>
      <ProblemAlert error={list.error} />
      {list.data && list.data.items.length === 0 && <p className="muted">No dead letters with status {status}.</p>}
      {list.data && list.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Direction</th>
              <th>Partner / event</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Created</th>
              <th>Resolved</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((d) => (
              <tr key={d.id}>
                <td>
                  <Link to={`${BASE}/dead-letters/${d.id}`}>{d.id}</Link>
                </td>
                <td>{d.direction}</td>
                <td>
                  {PARTNER_LABEL[d.partner]}
                  <br />
                  <code>
                    {d.eventType} {d.schemaVersion}
                  </code>
                </td>
                <td>{d.reason}</td>
                <td>
                  <StatusBadge status={d.status} />
                </td>
                <td>{formatDateTime(d.createdAt)}</td>
                <td>{d.resolvedAt ? `${formatDateTime(d.resolvedAt)} by ${d.resolvedByName ?? 'system'}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function DeadLetterPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['exchange', 'dead-letters', 'item', id], queryFn: () => exchangeApi.deadLetter(Number(id)) });
  const d = query.data;
  const [payloadText, setPayloadText] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);

  const refresh = (updated: DeadLetter) => {
    qc.setQueryData(['exchange', 'dead-letters', 'item', id], updated);
    void qc.invalidateQueries({ queryKey: ['exchange'] });
  };
  const replay = useMutation({
    mutationFn: (body: { payload?: unknown; note?: string }) => exchangeApi.replay(Number(id), body),
    onSuccess: refresh,
  });
  const discard = useMutation({ mutationFn: (n: string) => exchangeApi.discard(Number(id), { note: n }), onSuccess: refresh });

  function submitReplay(e: FormEvent) {
    e.preventDefault();
    setParseError(null);
    let payload: unknown = undefined;
    if (d?.direction === 'inbound' && payloadText !== null) {
      try {
        payload = JSON.parse(payloadText);
      } catch {
        setParseError('The corrected payload is not valid JSON.');
        return;
      }
    }
    replay.mutate({ ...(payload !== undefined && { payload }), ...(note && { note }) });
  }

  const envelope = d?.payload as { payload?: unknown } | undefined;
  const editable = payloadText ?? JSON.stringify(envelope?.payload ?? null, null, 2);

  return (
    <>
      <p>
        <Link to={`${BASE}?tab=dead-letters`}>← Dead letters</Link>
      </p>
      <ProblemAlert error={query.error} />
      {d && (
        <>
          <div className="row">
            <h1>Dead letter {d.id}</h1>
            <StatusBadge status={d.status} />
          </div>
          <DefinitionList
            items={[
              ['Direction', d.direction],
              ['Partner', PARTNER_LABEL[d.partner]],
              ['Interface', d.interfaceId ? `${d.interfaceId} ${d.schemaVersion}` : d.schemaVersion],
              ['Event', d.eventType],
              ['Reason', d.reason],
              ['Original', d.inboxId ? <Link to={`${BASE}/inbox/${d.inboxId}`}>inbox message {d.inboxId}</Link> : d.outboxId ? <Link to={`${BASE}/outbox/${d.outboxId}`}>outbox message {d.outboxId}</Link> : null],
              ['Created', formatDateTime(d.createdAt)],
              ['Resolved', d.resolvedAt ? `${formatDateTime(d.resolvedAt)} by ${d.resolvedByName ?? 'system'}` : null],
              ['Resolution note', d.resolutionNote],
              ['Replayed as', d.replayInboxId ? <Link to={`${BASE}/inbox/${d.replayInboxId}`}>inbox message {d.replayInboxId}</Link> : null],
            ]}
          />
          {d.errors.length > 0 && (
            <>
              <h2>Validation errors</h2>
              <ul>
                {d.errors.map((e, i) => (
                  <li key={i}>
                    {e.field && <code>{e.field}</code>} {e.message} <span className="muted">({e.code})</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <h2>Message</h2>
          <Json value={d.payload} />
          {d.status === 'open' && (
            <section className="panel">
              <h2>Resolve</h2>
              <form onSubmit={submitReplay}>
                {d.direction === 'inbound' && (
                  <Field label="Payload to replay" hint="Edit to correct the partner's payload; the envelope (message id, idempotency key, effective date) is kept so the replay is idempotent.">
                    <textarea rows={10} value={editable} onChange={(e) => setPayloadText(e.target.value)} />
                  </Field>
                )}
                {d.direction === 'outbound' && <p className="muted">Replay re-queues the outbound message for the worker with a fresh retry budget and the same idempotency key.</p>}
                <Field label="Note">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this is being replayed or discarded" />
                </Field>
                {parseError && (
                  <div role="alert" className="alert">
                    {parseError}
                  </div>
                )}
                <ProblemAlert error={replay.error ?? discard.error} />
                <div className="row">
                  <button type="submit" disabled={replay.isPending}>
                    Replay
                  </button>
                  <button type="button" className="secondary" disabled={discard.isPending || note.trim().length < 3} onClick={() => discard.mutate(note)}>
                    Discard (note required)
                  </button>
                </div>
              </form>
            </section>
          )}
        </>
      )}
    </>
  );
}

function ReconciliationTab() {
  const qc = useQueryClient();
  const { params, update } = useFilters();
  const partner = params.get('partner') ?? '';
  const list = useQuery({ queryKey: ['exchange', 'runs', partner], queryFn: () => exchangeApi.runs((partner || undefined) as Partner | undefined), refetchInterval: 15_000 });
  const run = useMutation({
    mutationFn: (p: Partner) => exchangeApi.reconcile(p),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exchange'] }),
  });
  return (
    <>
      <div className="row">
        <PartnerSelect value={partner} onChange={(v) => update({ partner: v })} />
        <span className="grow" />
        {PARTNERS.map((p) => (
          <button key={p} type="button" disabled={run.isPending} onClick={() => run.mutate(p)}>
            Reconcile {PARTNER_LABEL[p]} now
          </button>
        ))}
      </div>
      <p className="muted">
        The worker reconciles each partner nightly; a run pulls the partner's snapshot and compares it with what the register would publish. Differences are for CER review only.
      </p>
      <ProblemAlert error={list.error ?? run.error} />
      {list.data && list.data.items.length === 0 && <p className="muted">No reconciliation runs yet.</p>}
      {list.data && list.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Run</th>
              <th>Partner</th>
              <th>Status</th>
              <th>Started</th>
              <th>Triggered by</th>
              <th>Register</th>
              <th>Partner</th>
              <th>Matched</th>
              <th>Differences</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((r) => (
              <tr key={r.id}>
                <td>
                  <Link to={`${BASE}/reconciliation/${r.id}`}>{r.id}</Link>
                </td>
                <td>{PARTNER_LABEL[r.partner]}</td>
                <td>
                  <StatusBadge status={r.status} />
                </td>
                <td>{formatDateTime(r.startedAt)}</td>
                <td>{r.triggeredBy}</td>
                <td>{r.registerCount}</td>
                <td>{r.partnerCount}</td>
                <td>{r.matched}</td>
                <td>{r.differences}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function ReconciliationRunPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['exchange', 'runs', 'item', id], queryFn: () => exchangeApi.run(Number(id)) });
  const [filter, setFilter] = useState<'all' | 'unreviewed'>('unreviewed');
  const differences = useInfiniteQuery({
    queryKey: ['exchange', 'runs', 'item', id, 'differences', filter],
    queryFn: ({ pageParam }) => exchangeApi.differences(Number(id), { review: filter, after: pageParam, limit: DIFFERENCE_PAGE }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const review = useMutation({
    mutationFn: ({ diffId, reviewStatus, note }: { diffId: number; reviewStatus: Exclude<ReviewStatus, 'unreviewed'>; note?: string }) => exchangeApi.review(diffId, { reviewStatus, ...(note && { note }) }),
    onSuccess: (updated) => {
      qc.setQueriesData<InfiniteData<ReconciliationDifferencePage>>({ queryKey: ['exchange', 'runs', 'item', id, 'differences'] }, (prev) =>
        prev ? { ...prev, pages: prev.pages.map((p) => ({ ...p, items: p.items.map((i) => (i.id === updated.id ? updated : i)) })) } : prev,
      );
    },
  });
  const r = query.data;
  const items = (differences.data?.pages.flatMap((p) => p.items) ?? []).filter((i) => filter === 'all' || i.reviewStatus === 'unreviewed');
  return (
    <>
      <p>
        <Link to={`${BASE}?tab=reconciliation`}>← Reconciliation</Link>
      </p>
      <ProblemAlert error={query.error ?? differences.error ?? review.error} />
      {r && (
        <>
          <div className="row">
            <h1>
              Reconciliation run {r.id} — {PARTNER_LABEL[r.partner]}
            </h1>
            <StatusBadge status={r.status} />
          </div>
          <DefinitionList
            items={[
              ['Interface', r.interfaceId],
              ['Started', formatDateTime(r.startedAt)],
              ['Finished', formatDateTime(r.finishedAt)],
              ['Triggered by', r.triggeredBy],
              ['Register records', String(r.registerCount)],
              ['Partner records', String(r.partnerCount)],
              ['Matched', String(r.matched)],
              ['Differences', String(r.differences)],
              ['Error', r.error],
            ]}
          />
          <div className="row">
            <h2>Differences</h2>
            <span className="grow" />
            <select value={filter} onChange={(e) => setFilter(e.target.value as 'all' | 'unreviewed')} aria-label="Review filter">
              <option value="unreviewed">Unreviewed only</option>
              <option value="all">All</option>
            </select>
          </div>
          <p className="muted">
            <strong>missing there</strong>: on the register but not held by the partner · <strong>missing here</strong>: held by the partner but not on the register ·{' '}
            <strong>mismatch</strong>: both hold it with different values. Reviewing records CER's disposition; correcting the register happens through the normal correction flow.
          </p>
          {differences.isSuccess && items.length === 0 && !differences.hasNextPage && <p className="muted">Nothing to review.</p>}
          {items.map((d) => (
            <DifferenceRow key={d.id} d={d} onReview={(reviewStatus, note) => review.mutate({ diffId: d.id, reviewStatus, note })} busy={review.isPending} />
          ))}
          {differences.hasNextPage && (
            <p>
              <button type="button" className="secondary" disabled={differences.isFetchingNextPage} onClick={() => void differences.fetchNextPage()}>
                {differences.isFetchingNextPage ? 'Loading…' : `Show ${DIFFERENCE_PAGE} more`}
              </button>
            </p>
          )}
        </>
      )}
    </>
  );
}

function DifferenceRow({ d, onReview, busy }: { d: ReconciliationDifference; onReview: (s: Exclude<ReviewStatus, 'unreviewed'>, note?: string) => void; busy: boolean }) {
  const [note, setNote] = useState('');
  return (
    <section className="panel">
      <div className="row">
        <StatusBadge status={d.disposition} />
        <strong>{d.entityKind}</strong> {entityLink(d.entityKind, d.entityId)}
        <span className="grow" />
        <StatusBadge status={d.reviewStatus} />
      </div>
      {d.fields.length > 0 && (
        <p>
          Differs in: {d.fields.map((f) => <code key={f}>{f}</code>).reduce<ReactNode[]>((acc, el, i) => (i === 0 ? [el] : [...acc, ' ', el]), [])}
        </p>
      )}
      <details>
        <summary>Register value vs partner value</summary>
        <div className="cards">
          <div>
            <h3>Register</h3>
            <Json value={d.registerValue} />
          </div>
          <div>
            <h3>Partner</h3>
            <Json value={d.partnerValue} />
          </div>
        </div>
      </details>
      {d.reviewStatus === 'unreviewed' ? (
        <div className="row">
          <input className="grow" placeholder="Review note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="button" disabled={busy} onClick={() => onReview('accepted', note || undefined)}>
            Accept
          </button>
          <button type="button" disabled={busy} onClick={() => onReview('actioned', note || undefined)}>
            Actioned
          </button>
          <button type="button" className="secondary" disabled={busy} onClick={() => onReview('dismissed', note || undefined)}>
            Dismiss
          </button>
        </div>
      ) : (
        <p className="muted">
          {d.reviewStatus} by {d.reviewedByName ?? 'unknown'} at {formatDateTime(d.reviewedAt)}
          {d.reviewNote && ` — ${d.reviewNote}`}
        </p>
      )}
    </section>
  );
}
